import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FileSignature,
  Banknote,
  ChevronLeft,
  Coins,
  Inbox,
  Lock,
  LogOut,
  MessageSquareHeart,
} from 'lucide-react';
import { Button, Card, CardMeta, CardTitle, ErrorState, Skeleton } from '@/design/components';
import { useMe } from '@/auth/useMe';
import { fullName } from '@/auth/me.types';
import { signOut } from '@/auth/api';
import { ModuleGate } from '@/access/Gate';
import { useModules } from '@/access/useModule';
import { formatPhone } from '@/lib/phone';
import { errorMessage } from '@/lib/errors';
import { getTimezone } from '@/lib/dates';
import type { ModuleKey } from '@/access/modules';
import { PushCard } from '@/features/notifications/PushCard';
import {
  DEFAULT_QUIET,
  useMeMutations,
  useMyPrefs,
  useNotificationCategories,
  useQuietHours,
  type QuietHours,
} from './api';

/** האזור שלי: פרופיל, התראות, שעות שקט ויציאה (PRD 5.20). */
export function MePage() {
  const { me } = useMe();
  const modules = useModules();

  const categories = useNotificationCategories();
  const prefs = useMyPrefs(me?.employeeId);
  const quiet = useQuietHours(me?.employeeId);
  const { setPref, setQuiet, updateProfile } = useMeMutations(me?.employeeId);

  const [error, setError] = useState<string | null>(null);

  // כשהערך מהשרת מגיע או משתנה, הכרטיס נטען מחדש עם הערך החדש,
  // במקום לסנכרן state ב effect
  const loaded = quiet.data ?? DEFAULT_QUIET;
  const quietKey = [loaded.quiet_enabled, loaded.quiet_start, loaded.quiet_end].join('-');

  if (!me) return null;

  const visible = (categories.data ?? []).filter(
    (category) =>
      category.module_key === null || modules[category.module_key as ModuleKey] === true,
  );

  async function run(action: () => Promise<unknown>) {
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(errorMessage(caught));
    }
  }

  return (
    <div className="grid content-start gap-6 bg-cream p-4">
      <Card>
        <CardTitle>{fullName(me)}</CardTitle>
        <CardMeta dir="ltr">{formatPhone(me.phone)}</CardMeta>
        <CardMeta>
          {[me.branches.map((branch) => branch.name).join(', '), me.jobTitle, me.roleName]
            .filter(Boolean)
            .join(' · ')}
        </CardMeta>
      </Card>

      <ModuleGate module="games" fallback={null}>
        <Link
          to="/points"
          className="flex items-center gap-2 rounded-lg bg-raised p-4 text-body font-medium text-ink no-underline shadow-card"
        >
          <Coins size={20} strokeWidth={1.75} aria-hidden="true" />
          <span className="grow">הנקודות שלי</span>
          <ChevronLeft size={20} strokeWidth={1.75} aria-hidden="true" className="text-muted" />
        </Link>
      </ModuleGate>

      {/*
        כל קיצור והמפתח שלו. קודם ישב כאן שער אחד של "ops" על
        שניהם, ומי שכיבה "סקרים ומשוב" ראה אותו ממשיך להופיע
        לעובדים ומוביל למסך שמפנה הביתה.
      */}
      <ModuleGate module="ops_requests" fallback={null}>
        <Link
          to="/requests"
          className="flex items-center gap-2 rounded-lg bg-raised p-4 text-body font-medium text-ink no-underline shadow-card"
        >
          <Inbox size={20} strokeWidth={1.75} aria-hidden="true" />
          <span className="grow">הבקשות שלי</span>
          <ChevronLeft size={20} strokeWidth={1.75} aria-hidden="true" className="text-muted" />
        </Link>
      </ModuleGate>

      <ModuleGate module="ops_surveys" fallback={null}>
        <Link
          to="/surveys"
          className="flex items-center gap-2 rounded-lg bg-raised p-4 text-body font-medium text-ink no-underline shadow-card"
        >
          <MessageSquareHeart size={20} strokeWidth={1.75} aria-hidden="true" />
          <span className="grow">סקרים ומשוב</span>
          <ChevronLeft size={20} strokeWidth={1.75} aria-hidden="true" className="text-muted" />
        </Link>
      </ModuleGate>

      {/* מסמכים אישיים: חוזה לחתימה, ת"ז ופרטי בנק (מיגרציה 092).
          כאן ולא בתפריט הראשי — זה מסך שנכנסים אליו פעמיים בשנה. */}
      <ModuleGate module="hr_docs" fallback={null}>
        <Link
          to="/me/documents"
          className="flex items-center gap-2 rounded-lg bg-raised p-4 text-body font-medium text-ink no-underline shadow-card"
        >
          <FileSignature size={20} strokeWidth={1.75} aria-hidden="true" />
          <span className="grow">המסמכים שלי</span>
          <ChevronLeft size={20} strokeWidth={1.75} aria-hidden="true" className="text-muted" />
        </Link>
      </ModuleGate>

      <ModuleGate module="sales" fallback={null}>
        <Link
          to="/sales"
          className="flex items-center gap-2 rounded-lg bg-raised p-4 text-body font-medium text-ink no-underline shadow-card"
        >
          <Banknote size={20} strokeWidth={1.75} aria-hidden="true" />
          <span className="grow">המכירות שלי</span>
          <ChevronLeft size={20} strokeWidth={1.75} aria-hidden="true" className="text-muted" />
        </Link>
      </ModuleGate>

      <EmailCard
        key={me.email ?? 'no-email'}
        initial={me.email}
        onSave={(email) =>
          run(() => updateProfile.mutateAsync({ email, avatarPath: me.avatarPath }))
        }
      />

      <PushCard />

      <Card>
        <CardTitle>התראות</CardTitle>
        <CardMeta>אפשר לכבות סוגים שלא מעניינים. התראות חשובות תמיד מגיעות.</CardMeta>

        {error && (
          <p role="alert" className="m-0 text-body-sm text-bad">
            {error}
          </p>
        )}

        {(categories.isPending || prefs.isPending) && (
          <div className="grid gap-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        )}

        {categories.isError && <ErrorState onRetry={() => void categories.refetch()} />}

        <ul className="grid list-none gap-1 p-0">
          {visible.map((category) => {
            const value = prefs.data?.[category.key] ?? category.default_enabled;
            const id = `pref-${category.key}`;

            return (
              <li key={category.key} className="border-t border-line py-2 first:border-t-0">
                {category.is_mandatory ? (
                  <div className="flex items-start gap-3">
                    <Lock
                      size={20}
                      strokeWidth={1.75}
                      aria-hidden="true"
                      className="mt-0.5 shrink-0 text-muted"
                    />
                    <span className="grid gap-0.5">
                      <span className="text-body">{category.name}</span>
                      <span className="text-body-sm text-muted">הודעות חשובות תמיד מגיעות</span>
                    </span>
                  </div>
                ) : (
                  <label htmlFor={id} className="flex min-h-11 items-center gap-3">
                    <input
                      id={id}
                      type="checkbox"
                      checked={value}
                      onChange={(event) =>
                        run(() =>
                          setPref.mutateAsync({
                            key: category.key,
                            enabled: event.target.checked,
                          }),
                        )
                      }
                      className="size-5 shrink-0"
                    />
                    <span className="grid gap-0.5">
                      <span className="text-body">{category.name}</span>
                      {category.description && (
                        <span className="text-body-sm text-muted">{category.description}</span>
                      )}
                    </span>
                  </label>
                )}
              </li>
            );
          })}
        </ul>
      </Card>

      <QuietCard
        key={quietKey}
        value={quiet.data ?? DEFAULT_QUIET}
        isPending={quiet.isPending}
        onSave={(values) => run(() => setQuiet.mutateAsync(values))}
      />

      <Button variant="secondary" onClick={() => void signOut()}>
        <LogOut size={20} strokeWidth={1.75} aria-hidden="true" />
        יציאה מהחשבון
      </Button>
    </div>
  );
}

function EmailCard({
  initial,
  onSave,
}: {
  initial: string | null;
  onSave: (email: string | null) => void;
}) {
  const [email, setEmail] = useState(initial ?? '');
  const [saved, setSaved] = useState(false);

  const changed = (initial ?? '') !== email.trim();

  return (
    <Card>
      <CardTitle>הפרטים שלי</CardTitle>
      <div className="grid gap-1">
        <label htmlFor="me-email" className="text-label font-medium">
          מייל
        </label>
        <input
          id="me-email"
          type="email"
          dir="ltr"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            setSaved(false);
          }}
          className="min-h-11 rounded-sm border border-muted bg-surface px-3 text-body text-ink"
        />
      </div>
      <CardMeta>
        לשינוי שם או טלפון יש לפנות למנהל/ת. שינוי טלפון מעדכן גם את הכניסה לאפליקציה.
      </CardMeta>
      <div className="flex items-center gap-3">
        <Button
          variant="secondary"
          disabled={!changed}
          onClick={() => {
            onSave(email.trim() || null);
            setSaved(true);
          }}
        >
          שמירה
        </Button>
        {saved && !changed && (
          <span role="status" className="text-body-sm text-good">
            נשמר
          </span>
        )}
      </div>
    </Card>
  );
}

function QuietCard({
  value,
  isPending,
  onSave,
}: {
  value: QuietHours;
  isPending: boolean;
  onSave: (values: QuietHours) => void;
}) {
  const [draft, setDraft] = useState(value);

  const changed =
    draft.quiet_enabled !== value.quiet_enabled ||
    draft.quiet_start !== value.quiet_start ||
    draft.quiet_end !== value.quiet_end;

  // חלון שחוצה חצות הוא המקרה הרגיל, ולכן מוסבר במפורש
  const crossesMidnight = draft.quiet_start > draft.quiet_end;

  return (
    <Card>
      <CardTitle>שעות שקט</CardTitle>
      <CardMeta>בשעות האלה לא יישלחו התראות, חוץ מהחשובות. הן עדיין יחכו במרכז ההתראות.</CardMeta>

      {isPending ? (
        <Skeleton className="h-12 w-full" />
      ) : (
        <>
          <label className="flex min-h-11 items-center gap-3">
            <input
              type="checkbox"
              checked={draft.quiet_enabled}
              onChange={(event) =>
                setDraft((prev) => ({ ...prev, quiet_enabled: event.target.checked }))
              }
              className="size-5"
            />
            <span className="text-body">להפעיל שעות שקט</span>
          </label>

          {draft.quiet_enabled && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="grid gap-1">
                <label htmlFor="quiet-start" className="text-label font-medium">
                  מתחילות ב
                </label>
                <input
                  id="quiet-start"
                  type="time"
                  dir="ltr"
                  value={draft.quiet_start}
                  onChange={(event) =>
                    setDraft((prev) => ({ ...prev, quiet_start: event.target.value }))
                  }
                  className="min-h-11 rounded-sm border border-muted bg-surface px-3 text-body"
                />
              </div>
              <div className="grid gap-1">
                <label htmlFor="quiet-end" className="text-label font-medium">
                  מסתיימות ב
                </label>
                <input
                  id="quiet-end"
                  type="time"
                  dir="ltr"
                  value={draft.quiet_end}
                  onChange={(event) =>
                    setDraft((prev) => ({ ...prev, quiet_end: event.target.value }))
                  }
                  className="min-h-11 rounded-sm border border-muted bg-surface px-3 text-body"
                />
              </div>
            </div>
          )}

          {draft.quiet_enabled && (
            <CardMeta>
              {crossesMidnight
                ? `מ ${draft.quiet_start} בלילה עד ${draft.quiet_end} בבוקר, לפי שעון ${getTimezone()}.`
                : `מ ${draft.quiet_start} עד ${draft.quiet_end}, באותו יום.`}
            </CardMeta>
          )}

          <div>
            <Button variant="secondary" disabled={!changed} onClick={() => onSave(draft)}>
              שמירה
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}
